import React, { ReactNode, useCallback, useEffect, useState } from "react"
import styled, { css } from "styled-components"
import {
  LabelH1,
  LabelH2,
  LabelH3,
  LabelH4,
  P,
} from "@jobintrans/base-ui.components.atoms.typography"
import { Box, Row, common } from "@jobintrans/base-ui.components.atoms._atoms"
import Accordion from "@jobintrans/base-ui.components.molecules.accordion"
import { PLANS_ARRAY } from "@jobintrans/base-ui.data"
import List from "@jobintrans/base-ui.components.molecules.list"
import SelectRow from "@jobintrans/base-ui.components.molecules.select-row"
import { DiscountType } from "@jobintrans/base-ui.types.api"
import Input from "@jobintrans/base-ui.components.atoms.input"
import { IconButton } from "@jobintrans/base-ui.components.atoms.button"
import { ToolsService } from "@jobintrans/base-ui.services.api-service"
import { formatPrice } from "@jobintrans/base-ui.utils.number"
import { BaseStyles } from "@jobintrans/base-ui.types.base-styles"

const PriceTag = ({
  discount,
  children,
  currency,
  $alignItems = "flex-start",
  $opacity = 1,
  $marginLeft,
  $color,
}: {
  discount: number | null
  children: number
  currency: string
  $color?: string
  $alignItems?: string
  $opacity?: number
  $marginLeft?: string
}) => {
  return (
    <Box $width="max-content" $opacity={$opacity} $marginLeft={$marginLeft}>
      {discount !== null ? (
        <Row $flexDirection="column" $alignItems={$alignItems}>
          <CrossLabelH4 $color={$color}>
            {formatPrice(children)} <span>{currency}</span>
          </CrossLabelH4>
          <LabelH2 $marginBottom="0" $color={$color ?? "primary"}>
            {formatPrice(discount)} <span>{currency}</span>
          </LabelH2>
        </Row>
      ) : (
        <LabelH1 $marginBottom="0" $color={$color}>
          {formatPrice(children)} <span>{currency}</span>
        </LabelH1>
      )}
    </Box>
  )
}

const PricingSection = ({
  initialCode,
  setParams,
  button,
  translator: t,
  currency,
  defaultPlan = 2,
  showTest = false,
  showDiscount = true,
  fullSize = true,
  ...rest
}: {
  initialCode?: string
  setParams?: ({
    code,
    type,
    period,
    plan,
  }: {
    code?: string
    type?: string
    period: string
    plan: string
  }) => void
  button?: ReactNode
  translator: (e: string) => string
  defaultPlan?: number
  fullSize?: boolean
  showTest?: boolean
  showDiscount?: boolean
  currency: "pl" | "eur" | "usd"
} & BaseStyles) => {
  const [type, setType] = useState<"offer">("offer")
  const [plan, setPlan] = useState(PLANS_ARRAY[type][defaultPlan])
  const [period, setPeriod] = useState("1")
  const [code, setCode] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [discount, setDiscount] = useState<null | DiscountType>(null)

  const handleChangeCode = useCallback((value: string) => {
    setError(false)
    setCode(value)
    setDiscount(null)
  }, [])

  const applyCodeToPromotion = useCallback(
    async (newCode?: string) => {
      try {
        setError(false)
        setLoading(true)
        setDiscount(null)
        const discount = await ToolsService.getDiscount(newCode ?? code.toUpperCase())
        setDiscount(discount)

        const { type: discountType, time, value, plans } = discount

        if (discountType === "percentage") {
          if (plans.includes("premium")) {
            setPlan(PLANS_ARRAY[type][2])
          } else if (plans.includes("extended")) {
            setPlan(PLANS_ARRAY[type][1])
          } else if (plans.includes("basic")) {
            setPlan(PLANS_ARRAY[type][0])
          }
        } else {
          if (value === 1) {
            setPlan(PLANS_ARRAY[type][0])
          } else if (value === 2) {
            setPlan(PLANS_ARRAY[type][1])
          } else {
            setPlan(PLANS_ARRAY[type][2])
          }
        }

        if (type === "offer") {
          if (time.includes("m6")) {
            setPeriod("6")
          } else if (time.includes("m3")) {
            setPeriod("3")
          } else {
            setPeriod("1")
          }
        } else {
          setPeriod("1")
        }
      } catch (e) {
        setError(true)
        console.log("[ERROR ToolsService.getDiscount]:", e)
        return
      } finally {
        setLoading(false)
      }
    },
    [type, code],
  )

  const calculateDiscount = (
    price: number,
    additionalDiscount = 100,
    months = 1,
    showDiscount = true,
  ) => {
    if (!showDiscount) return null

    let finalDiscount = 100
    if (discount) {
      finalDiscount = discount?.type === "post" ? 0 : finalDiscount - discount.value
    }

    return months * (price * (finalDiscount / 100) * (additionalDiscount / 100))
  }

  useEffect(() => {
    if (initialCode && initialCode !== "") {
      setCode(initialCode)
      applyCodeToPromotion(initialCode)
    }
  }, [initialCode])

  useEffect(() => {
    if (setParams) {
      setParams({
        code: discount?.code,
        type: type,
        plan: plan.slug,
        period: plan.slug === PLANS_ARRAY[type][3].slug ? "1" : period,
      })
    }
  }, [type, discount, plan, period])

  const disableBasic =
    !!discount &&
    ((discount?.type === "post" && discount?.value !== 1) || !discount?.plans.includes("basic"))
  const disableExtended =
    !!discount &&
    ((discount?.type === "post" && discount?.value !== 2) || !discount?.plans.includes("extended"))
  const disablePremium =
    !!discount &&
    ((discount?.type === "post" && discount?.value !== 3) || !discount?.plans.includes("premium"))

  const isM1Disabled = !!discount && !discount.time.includes("m1")
  const isM3Disabled = !!discount && !discount.time.includes("m3")
  const isM6Disabled = !!discount && !discount.time.includes("m6")

  const isBasicDiscount = discount
    ? (discount?.type === "post" && discount?.value === 1) ||
      (discount?.type === "percentage" && discount.plans.includes("basic"))
    : false
  const isExtendedDiscount = discount
    ? (discount?.type === "post" && discount?.value === 2) ||
      (discount?.type === "percentage" && discount.plans.includes("extended"))
    : false
  const isPremiumDiscount = discount
    ? (discount?.type === "post" && discount?.value === 3) ||
      (discount?.type === "percentage" && discount.plans.includes("premium"))
    : false

  return (
    <Wrapper {...rest}>
      {showDiscount && (
        <PromoBannerWrapper>
          <Box>
            <LabelH3 $color="primary" $display="block" $marginBottom="XXS">
              {t("promotion.title")}
            </LabelH3>
            <P $marginBottom="0" $small>
              {t("promotion.text")}
            </P>
          </Box>
          <PromoBannerInputContent>
            <Box $flex={1}>
              <Input
                placeholder={t("promotion.placeholder")}
                name="name"
                value={code.toUpperCase()}
                defaultValue={code.toUpperCase()}
                onChange={({ value }) => handleChangeCode(value)}
              />
            </Box>
            <IconButton
              icon={error ? "close" : discount ? "check" : "send"}
              loading={loading}
              onClick={() => applyCodeToPromotion()}
              ariaLabel={error ? "Close" : discount ? "Check" : "Send"}
              $size="45px"
              $marginLeft="XXS"
              $borderRadius="borderRadius"
              $background={error ? "red" : discount ? "green" : "primary"}
              $color="white"
            />
          </PromoBannerInputContent>
        </PromoBannerWrapper>
      )}
      <StyledGrid $fullSize={fullSize}>
        <Accordion
          title={t(`plans.${PLANS_ARRAY[type][0].slug}.title`)}
          isCheckable
          checked={plan.slug === PLANS_ARRAY[type][0].slug}
          onClick={() => setPlan(PLANS_ARRAY[type][0])}
          disabled={disableBasic}
          $withBackground
        >
          <PriceTag
            discount={
              isBasicDiscount
                ? calculateDiscount(PLANS_ARRAY[type][0].price[currency].value, 100, 1, !!discount)
                : null
            }
            currency={PLANS_ARRAY[type][0].price[currency].currency}
            $marginLeft={!fullSize ? "auto" : undefined}
          >
            {PLANS_ARRAY[type][0].price[currency].value}
          </PriceTag>
          {fullSize && (
            <List
              list={PLANS_ARRAY[type][0].list.map(el => t(`advantages.${el}`))}
              $marginTop="S"
            />
          )}
        </Accordion>
        <Accordion
          title={t(`plans.${PLANS_ARRAY[type][1].slug}.title`)}
          isCheckable
          checked={plan.slug === PLANS_ARRAY[type][1].slug}
          onClick={() => setPlan(PLANS_ARRAY[type][1])}
          disabled={disableExtended}
          $withBackground
        >
          <PriceTag
            discount={
              isExtendedDiscount
                ? calculateDiscount(PLANS_ARRAY[type][1].price[currency].value, 100, 1, !!discount)
                : null
            }
            currency={PLANS_ARRAY[type][0].price[currency].currency}
            $marginLeft={!fullSize ? "auto" : undefined}
          >
            {PLANS_ARRAY[type][1].price[currency].value}
          </PriceTag>
          {fullSize && (
            <List
              list={PLANS_ARRAY[type][1].list.map(el => t(`advantages.${el}`))}
              $marginTop="S"
            />
          )}
        </Accordion>
        <Box>
          <Accordion
            title={t(`plans.${PLANS_ARRAY[type][2].slug}.title`)}
            helperText={fullSize ? t(`common.recommended`) : undefined}
            isCheckable
            checked={plan.slug === PLANS_ARRAY[type][2].slug}
            onClick={() => setPlan(PLANS_ARRAY[type][2])}
            disabled={disablePremium}
            $withBackground
          >
            <PriceTag
              $marginLeft={!fullSize ? "auto" : undefined}
              discount={
                isPremiumDiscount
                  ? calculateDiscount(
                      PLANS_ARRAY[type][2].price[currency].value,
                      100,
                      1,
                      !!discount,
                    )
                  : null
              }
              currency={PLANS_ARRAY[type][0].price[currency].currency}
            >
              {PLANS_ARRAY[type][2].price[currency].value}
            </PriceTag>
            {fullSize && (
              <List
                $marginTop="S"
                list={PLANS_ARRAY[type][2].list.map(el => t(`advantages.${el}`))}
              />
            )}
          </Accordion>
        </Box>
      </StyledGrid>
      {showTest && (
        <Accordion
          title={t(`plans.${PLANS_ARRAY[type][3].slug}.title`)}
          icon="lightbulb"
          isCheckable
          checked={plan.slug === PLANS_ARRAY[type][3].slug}
          onClick={() => setPlan(PLANS_ARRAY[type][3])}
          disabled={disableBasic}
          $marginTop="S"
          $background="primaryBackground"
          $borderColor="primary"
          $outerBorderColor="border"
        >
          <PriceTag
            $marginLeft={!fullSize ? "auto" : undefined}
            discount={null}
            currency={PLANS_ARRAY[type][3].price[currency].currency}
          >
            {PLANS_ARRAY[type][3].price[currency].value}
          </PriceTag>
          <P $marginBottom="0" $marginTop="S" $small>
            {t(`plans.${PLANS_ARRAY[type][3].slug}.text`)}
          </P>
        </Accordion>
      )}
      {fullSize && plan.slug !== PLANS_ARRAY[type][3].slug && (
        <DiscountList>
          <SelectRow
            checked={period === "1"}
            label={
              <Row>
                <LabelH3 $marginBottom="0">{t("periods.1m")}</LabelH3>
              </Row>
            }
            value={
              <PriceTag
                discount={calculateDiscount(plan.price[currency].value, 100, 1, !!discount)}
                currency={plan.price[currency].currency}
                $alignItems="flex-end"
                $opacity={isM1Disabled ? 0 : 1}
              >
                {plan.price[currency].value}
              </PriceTag>
            }
            onClick={() => setPeriod("1")}
            $withBackground
            disabled={isM1Disabled}
          />
          <SelectRow
            checked={period === "3"}
            label={
              <Row $alignItems="center">
                <LabelH3 $marginBottom="0">{t("periods.3m")}</LabelH3>
                {!discount && (
                  <DiscountTag $marginBottom="0" $marginLeft="S" $color="red">
                    -10%
                  </DiscountTag>
                )}
              </Row>
            }
            value={
              <PriceTag
                discount={calculateDiscount(plan.price[currency].value, 90, 3)}
                currency={plan.price[currency].currency}
                $alignItems="flex-end"
                $opacity={isM3Disabled ? 0 : 1}
              >
                {Math.round(3 * plan.price[currency].value)}
              </PriceTag>
            }
            onClick={() => setPeriod("3")}
            $withBackground
            disabled={!!discount && !discount.time.includes("m3")}
          />
          <SelectRow
            checked={period === "6"}
            label={
              <Row $alignItems="center">
                <LabelH3 $marginBottom="0">{t("periods.6m")}</LabelH3>
                {!discount && (
                  <DiscountTag $marginBottom="0" $marginLeft="S" $color="red">
                    -20%
                  </DiscountTag>
                )}
              </Row>
            }
            value={
              <PriceTag
                discount={calculateDiscount(plan.price[currency].value, 80, 6)}
                currency={plan.price[currency].currency}
                $alignItems="flex-end"
                $opacity={isM6Disabled ? 0 : 1}
              >
                {Math.round(6 * plan.price[currency].value)}
              </PriceTag>
            }
            onClick={() => setPeriod("6")}
            disabled={!!discount && !discount.time.includes("m6")}
            $withBackground
          />
        </DiscountList>
      )}
      {button && <Row $marginTop="S">{button}</Row>}
      <P $textAlign="center" $width="100%" $small $marginTop="S">
        {t("common.vat")}
      </P>
    </Wrapper>
  )
}

export default PricingSection

const DiscountTag = styled(LabelH4)`
  ${common};
  margin-bottom: 0;
  background: ${({ theme }) => theme.color.redBackground};
  padding: ${({ theme }) => theme.space.XXS} ${({ theme }) => theme.space.XS};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
`

const CrossLabelH4 = styled(LabelH4)`
  ${common};
  margin-bottom: 0;
  text-decoration: line-through;
`

const PromoBannerInputContent = styled(Row)`
  @media (max-width: 700px) {
    width: 100%;
  }
`

const PromoBannerWrapper = styled(Box)`
  margin-bottom: calc(${({ theme }) => theme.space.L} * 1.5);
  padding: ${({ theme }) => theme.space.S};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  background: ${({ theme }) => theme.color.background};
  display: flex;
  justify-content: space-between;
  align-items: center;
  grid-gap: ${({ theme }) => theme.space.S};

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: start;
  }
`

const DiscountList = styled(Box)`
  display: flex;
  flex-direction: column;
  grid-gap: ${({ theme }) => theme.space.XS};
  margin-top: ${({ theme }) => theme.space.S};
`

const StyledGrid = styled(Box)<{
  $fullSize: boolean
}>`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: ${({ theme }) => theme.space.XS};
  margin-top: 40px;

  > div:nth-child(3) {
    > div {
      height: 100%;
    }
  }

  ${({ $fullSize }) =>
    !$fullSize &&
    css`
      margin-top: 0;
      grid-template-columns: 1fr;
    `};

  @media (max-width: 1000px) {
    margin-top: 0;
    grid-template-columns: 1fr;

    > div:nth-child(3) {
      padding-top: 34px;
    }
  }
`

const Wrapper = styled(Box)``
